<template>
  <section class="hero">
    <div class="hero-body">
      <div class="container" v-if="recipe">
        <div class="columns">
          <div class="column is-narrow">
            <h1 class="title">
              {{ recipe.name }}
              <a class="mwf-link" v-if="recipe.link" :href="recipe.link" target="_blank">
                <span class="icon">
                  <font-awesome-icon v-if="youTubeLink(recipe.link)" :icon="['fab', 'youtube']" />
                  <font-awesome-icon v-else icon="external-link-alt" />
                </span>
              </a>
              <div class="button-group">
                <button class="button is-white" @click="routeEditFoodItem({ type: 'Recipe', id })">
                  <b-icon
                    pack="fas"
                    icon="pencil-alt"
                    size="is-small" />
                </button>
                <button class="button is-white" @click="deleteRecipe">
                  <b-icon
                    pack="fas"
                    icon="trash-alt"
                    size="is-small" />
                </button>
              </div>
            </h1>
            <h2 v-if="prepTime || servings" class="subtitle">
              <template v-if="prepTime">
                <span class="icon">
                  <font-awesome-icon size="xs" icon="stopwatch" />
                </span>
                {{prepTime}}
              </template>
              <template v-if="servings">
                <span class="icon">
                  <font-awesome-icon size="xs" icon="utensils" />
                </span>
                {{servings}}
              </template>
            </h2>
            <h2 v-else>Recipe</h2>
            <div class="tags">
              <span v-for="cat in recipe.categories" :key="cat" class="tag is-info">
                {{cat}}
              </span>
            </div>
          </div>
          <div v-if="youTubeLink(recipe.link)" class="column is-offset-1">
            <div class="video-responsive">
              <iframe width="560" height="315" :src="youTubeLink(recipe.link)" frameborder="0"
              allow="autoplay; encrypted-media" allowfullscreen></iframe>
            </div>
          </div>
        </div>
        <p class="mwf-desc">
          <!-- TODO if this is really long, there should be a button to open a modal -->
          {{recipe.description}}
        </p>
        <hr>
        <div class="content">
          <div class="columns">
            <div class="column is-narrow">
              <h4 class="title is-4">Ingredients</h4>
              <h6 class="subtitle is-6"><a role="button" @click="sendToShoppingListClicked">Send to Shopping List</a></h6>
              <ul class="ingredient-list">
                <li v-for="(ingredient, index) in recipe.ingredients" :key="index">
                  {{ingredient}}
                </li>
              </ul>
            </div>
            <div v-if="recipe.directions && recipe.directions.length" class="column is-offset-1">
              <h4 class="title is-4">Directions</h4>
              <h6 class="subtitle is-6"><a role="button" @click="clearDirectionChecks">Clear</a></h6>
              <p v-for="(direction, index) in recipe.directions" :key="index">
                <large-checkbox :val="index" v-model="directionChecks" checkbox-side="left">{{direction}}</large-checkbox>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Invalid recipe id -->
      <div class="container" v-else-if="error">
        <h1 class="title">Invalid recipe id</h1>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import LargeCheckbox from '@/components/LargeCheckbox';
import linksMixin from '@/mixins/links';
import routingMixin from '@/mixins/routing';
import notifyMixin from '@/mixins/notify';
import { minsToString } from '../../utils/time';
import ShoppingListItem from '../../shared/ShoppingListItem';
import { AddItemsAction } from '../shopping-list/actions';

export default {
  name: 'recipe',
  mixins: [linksMixin, routingMixin, notifyMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      error: null,
      directionChecks: [],
    };
  },
  watch: {
    $route() {
      this.fetchRecipe();
    },
  },
  mounted() {
    this.fetchRecipe();
  },
  methods: {
    sendToShoppingListClicked() {
      const self = this;
      this.$dialog.confirm({
        title: 'Recipe Ingredients',
        message: 'Send to <b>default</b> shopping list?',
        confirmText: 'Send',
        type: 'is-info',
        hasIcon: true,
        onConfirm() {
          self.sendToShoppingList();
        },
      });
    },
    sendToShoppingList() {
      const items = this.recipe.ingredients.map(i => (new ShoppingListItem({
        foodItemId: this.recipe.id,
        description: i,
      })));

      const action = new AddItemsAction({
        component: this,
        list: items,
      });

      action.execute().then(() => this.$toast.open({
        message: 'Successfully added to shopping list',
        type: 'is-success',
      })).then(() => {
        this.error = null;
      }).catch((err) => {
        const message = (err.graphQLErrors && err.graphQLErrors.length ?
          err.graphQLErrors[0].message : err.message);

        this.$toast.open({
          message,
          type: 'is-warning',
        });
      });
    },
    clearDirectionChecks() {
      this.directionChecks = [];
    },
    fetchRecipe() {
      return this.$store.dispatch('recipe/fetchRecipe', this.id).catch((err) => {
        this.error = err.message;
      });
    },
    deleteRecipe() {
      this.$dialog.confirm({
        title: 'Delete Recipe',
        message: 'Are you sure?',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch('recipe/deleteRecipe', this.id)
          .then(() => this.routeBrowse({ noBack: true }))
          .catch((err) => {
            this.error = err.message;
          });
        },
      });
    },
  },
  computed: {
    ...mapGetters('recipe', ['recipe']),
    prepTime() {
      return minsToString(this.recipe.prepTimeMin);
    },
    servings() {
      if (this.recipe.servings > 1) {
        return `${this.recipe.servings} servings`;
      } else if (this.recipe.servings === 1) {
        return `${this.recipe.servings} serving`;
      }

      return '';
    },
  },
  components: {
    'large-checkbox': LargeCheckbox,
    'font-awesome-icon': FontAwesomeIcon,
  },
};
</script>

<style lang="sass" scoped>
.button-group
  display: inline-block

.mwf-link
  font-size: 1.1rem
  vertical-align: super

.mwf-desc
  max-height: 6rem
  overflow: auto

.ingredient-list
  margin-left: 1em

.video-responsive
    overflow: hidden
    padding-bottom: 56.25%
    position: relative
    height: 0

.video-responsive iframe
      left: 0
      top: 0
      height: 100%
      width: 100%
      position: absolute

</style>



// WEBPACK FOOTER //
// src/components/recipe/Recipe.vue